import {
  Accordion,
  EHeadingStyleVariant,
  ETextStyleVariant,
  Heading,
  Text,
} from '@outdoorsyco/bonfire';
import React from 'react';
import { useIntl } from 'react-intl';

import MaxWidth from '@/components/common/layout/MaxWidth';

type TGroupFaq = {
  question: string;
  answer: string;
};

export const GroupRvFaqs: React.FC = () => {
  const intl = useIntl();

  const faqs: TGroupFaq[] = [
    {
      question: intl.formatMessage({
        id: 'ggMZY6',
        defaultMessage: 'I work for an organization — how do I submit a booking request?',
        description: 'Group RV FAQ question about how to submit booking request',
      }),
      answer: intl.formatMessage({
        id: 'vRLFW+',
        defaultMessage: `That’s exactly what we’re here for! The easiest way to get started is by filling out our group
booking inquiry form. Just tell us a few details about your event, workforce, or production, and
one of our dedicated Group Booking Concierges will reach out. From there, you’ll have one
point of contact who handles everything — from finding the right rigs to managing delivery,
insurance, and logistics Easy.`,
        description: 'Group RV FAQ answer for booking request',
      }),
    },
    {
      question: intl.formatMessage({
        id: 'brqf/b',
        defaultMessage: 'I don’t have hookups at my venue — which RVs will work?',
        description: 'Group RV FAQ question about hookups and RV types',
      }),
      answer: intl.formatMessage({
        id: 'DfZK8p',
        defaultMessage: `No hookups? No problem. That’s a super common setup for festivals, weddings, and film
shoots. It’s called “dry camping,” and plenty of modern RVs are built for it. When you chat with
your concierge, just let them know your location setup. We’ll line up RVs with onboard
generators, solar panels, large freshwater tanks, and even coordinate mobile dumping so your
guests or crew can stay comfortably — no outlets required.`,
        description: 'Group RV FAQ answer for hookups',
      }),
    },
    {
      question: intl.formatMessage({
        id: 'D+f+9P',
        defaultMessage: 'Do you offer insurance for the vehicles I rent?',
        description: 'Group RV FAQ question about insurance',
      }),
      answer: intl.formatMessage({
        id: 'rw8hTH',
        defaultMessage: `Absolutely. Every rental on Outdoorsy is backed by top-tier insurance coverage, because trust
and safety are kind of our whole thing. Your concierge will walk you through protection options,
help you choose the right coverage, and make sure you’ve got total peace of mind before
anyone hits the road (or park).`,
        description: 'Group RV FAQ answer for insurance',
      }),
    },
    {
      question: intl.formatMessage({
        id: 'Enb/IU',
        defaultMessage: 'Can you work within my event budget? What variables should I know about?',
        description: 'Group RV FAQ question about budget and variables',
      }),
      answer: intl.formatMessage({
        id: 'N8RoTI',
        defaultMessage: `Yes! We’re great at helping you make the most of your budget — and our marketplace gives us
a lot of flexibility to do that. A few factors can move the needle:
● Vehicle Type: We can mix and match, from luxury Class A motorhomes to
budget-friendly travel trailers.
● Timing: Rates are usually lower during off-season months.
● Duration: The longer you rent, the more options we can work with.
Just tell your concierge what you’re aiming for, and we’ll build a fleet plan that fits your goals
and your wallet.`,
        description: 'Group RV FAQ answer for budget',
      }),
    },
    {
      question: intl.formatMessage({
        id: 'mtm5hO',
        defaultMessage: 'What do you mean by “concierge service”?',
        description: 'Group RV FAQ question about concierge service',
      }),
      answer: intl.formatMessage({
        id: 'ZrJLvl',
        defaultMessage: `It means you get one dedicated Outdoorsy pro who handles everything. Instead of emailing 10
different RV owners, you tell us what you need — your concierge handles the rest. They’ll find
the perfect rigs, coordinate delivery, handle contracts and insurance, and keep everything
running smoothly. One call, one contact, zero chaos.`,
        description: 'Group RV FAQ answer for concierge service',
      }),
    },
    {
      question: intl.formatMessage({
        id: 'r2wRkk',
        defaultMessage: 'Is there a minimum number of RVs required for this service?',
        description: 'Group RV FAQ question about minimum RV count',
      }),
      answer: intl.formatMessage({
        id: '6mvJ+T',
        defaultMessage: `Yep — our dedicated concierge service is designed for groups needing five or more RVs. If
you need fewer, no worries! You can still browse and book incredible RVs directly from verified
owners on our site.`,
        description: 'Group RV FAQ answer for minimum RVs',
      }),
    },
    {
      question: intl.formatMessage({
        id: 'KT427p',
        defaultMessage: 'Do I have to drive or tow the RVs myself?',
        description: 'Group RV FAQ question about driving or towing',
      }),
      answer: intl.formatMessage({
        id: 'cTodrW',
        defaultMessage: `Not at all. In fact, most of our group rentals are fully delivered and set up for you. We’ll
coordinate with our professional owners and Superhosts to have everything in place before your
guests or team arrive. You focus on the event — we’ll handle the parking brake.`,
        description: 'Group RV FAQ answer for driving/towing',
      }),
    },
    {
      question: intl.formatMessage({
        id: 'QxnKO+',
        defaultMessage: 'What kinds of RVs can I rent for my group?',
        description: 'Group RV FAQ question about RV types',
      }),
      answer: intl.formatMessage({
        id: 'CgltYL',
        defaultMessage: `Pretty much anything on wheels. Outdoorsy has one of the largest and newest inventories in
the U.S. — from luxury Class A motorhomes (perfect for talent trailers), to cozy Class C’s, sleek
campervans, and entire fleets of modern travel trailers. Your concierge will help you build the
perfect lineup for your crew.`,
        description: 'Group RV FAQ answer for RV types',
      }),
    },
    {
      question: intl.formatMessage({
        id: 'PWwqhp',
        defaultMessage:
          'What are our payment options? Can our organization pay by invoice or purchase order?',
        description: 'Group RV FAQ question about payment options',
      }),
      answer: intl.formatMessage({
        id: '+0iWb8',
        defaultMessage: `Our standard online booking platform accepts all major credit cards and PayPal. But we know
that organizations, production companies, and event planners often need a different setup.
That’s a big part of what makes our concierge service so helpful. Your dedicated concierge can
coordinate invoicing, purchase orders, or custom payment arrangements to make the admin
side easy — even for multi-unit bookings.`,
        description: 'Group RV FAQ answer for payments',
      }),
    },
  ];

  return (
    <section className="py-12 bg-white  lg:py-16">
      <div className="flex justify-center lg:px-12">
        <MaxWidth className="gap-6">
          <Heading
            variant={EHeadingStyleVariant.H3}
            className="mb-4 leading-tight text-gray-900 font-[abril-titling] !text-[40px]">
            Your group booking questions, answered.
          </Heading>

          <Accordion>
            {faqs.map(faq => (
              <Accordion.Item
                key={faq.question}
                divider
                title={faq.question}
                headerClassName="px-0 py-4"
                className="text-gray-800">
                <Text variant={ETextStyleVariant.MediumRegular} className="whitespace-pre-line">
                  {faq.answer}
                </Text>
              </Accordion.Item>
            ))}
          </Accordion>
        </MaxWidth>
      </div>
    </section>
  );
};
